/** How long a finger must rest before the press counts, matching WebKit's own long press. */
const LONG_PRESS_MS = 500;

/** Past this much travel the finger is scrolling or selecting, not pressing. */
const MOVE_TOLERANCE_PX = 10;

export type LongPressTracker = ReturnType<typeof createLongPressTracker>;

/**
 * Recognises a long press: one finger resting in place for {@link LONG_PRESS_MS}.
 *
 * The gesture completes while the finger is still down, so it reports through
 * `onLongPress` rather than a return value, and fires at most once per touch.
 */
export function createLongPressTracker(onLongPress: () => void) {
  let tracked: {
    id: number;
    x: number;
    y: number;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;

  const cancel = () => {
    if (tracked) clearTimeout(tracked.timer);
    tracked = null;
  };

  return {
    start(touch: Touch) {
      cancel();
      tracked = {
        id: touch.identifier,
        x: touch.clientX,
        y: touch.clientY,
        timer: setTimeout(() => {
          tracked = null;
          onLongPress();
        }, LONG_PRESS_MS),
      };
    },

    move(touch: Touch) {
      if (!tracked || touch.identifier !== tracked.id) return;

      if (Math.hypot(touch.clientX - tracked.x, touch.clientY - tracked.y) > MOVE_TOLERANCE_PX) {
        cancel();
      }
    },

    cancel,
  };
}
